import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import { defineComponent } from "vue";
import { p } from "@/utils/props-builder";
import Skeleton from "./Skeleton";

const props = {
  /**
   * Show tabs sidebar
   * @default true
   */
  showTabs: p.boolean(true),

  /**
   * Number of tab groups
   * @default 2
   */
  tabGroups: p.number(2),

  /**
   * Tabs per group
   * @default 5
   */
  tabsPerGroup: p.number(5),

  /**
   * Show footer buttons
   * @default true
   */
  showButtons: p.boolean(true),
} satisfies ComponentPropsOptions;

export type AgentFormSkeletonProps = ExtractPublicPropTypes<typeof props>;

/**
 * AgentFormSkeleton - Loading placeholder for agent create/edit pages
 *
 * Specialized skeleton for agent forms with sidebar navigation:
 * - Left sidebar with grouped tabs (Configuration, Tools, etc.)
 * - Right content area with form fields
 * - Footer with action buttons
 *
 * @example
 * ```tsx
 * // Basic usage
 * <AgentFormSkeleton />
 *
 * // Custom tab configuration
 * <AgentFormSkeleton tabGroups={3} tabsPerGroup={4} />
 *
 * // Without tabs sidebar
 * <AgentFormSkeleton showTabs={false} />
 *
 * // With QueryLoader
 * <QueryLoader queries={[agentsQuery, pricingQuery]}>
 *   <QueryLoader.Loading>
 *     <AgentFormSkeleton />
 *   </QueryLoader.Loading>
 *   <Card>
 *     <AgentForm>...</AgentForm>
 *   </Card>
 * </QueryLoader>
 * ```
 */
const AgentFormSkeleton = defineComponent({
  name: "AgentFormSkeleton",
  props,
  setup(props) {
    return () => (
      <div class="h-[calc(100vh-5.5rem)] overflow-hidden rounded border bg-card">
        <div class="flex h-full">
          {/* Left sidebar with tabs */}
          {props.showTabs && (
            <div class="flex w-64 shrink-0 flex-col border-r bg-surface-2">
              {/* Sidebar header */}
              <div class="flex h-12 shrink-0 items-center border-b px-4">
                <Skeleton class="h-5 w-32 rounded" />
              </div>

              {/* Tab groups */}
              <div class="flex-1 overflow-auto p-4">
                {Array.from({ length: props.tabGroups }).map((_, groupIndex) => (
                  <div key={`group-${groupIndex}`} class={groupIndex > 0 ? "mt-6" : ""}>
                    {/* Group title */}
                    <Skeleton class="mb-3 h-3 w-24 rounded" />

                    {/* Tab items */}
                    <div class="space-y-1">
                      {Array.from({ length: props.tabsPerGroup }).map((_, tabIndex) => (
                        <div
                          key={`tab-${groupIndex}-${tabIndex}`}
                          class="flex items-center gap-3 px-4 py-2"
                        >
                          <Skeleton class="h-4 w-4 shrink-0 rounded" />
                          <Skeleton class="h-4 flex-1 rounded" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Sidebar footer */}
              {props.showButtons && (
                <div class="mt-auto border-t p-4">
                  <Skeleton class="h-10 w-full rounded-md" />
                </div>
              )}
            </div>
          )}

          {/* Right content area */}
          <div class="flex min-h-full w-full grow flex-col overflow-hidden">
            {/* Content header */}
            <div class="flex h-12 shrink-0 items-center border-b px-4">
              <Skeleton class="h-5 w-40 rounded" />
            </div>

            {/* Form content */}
            <div class="flex-1 space-y-6 overflow-auto p-6">
              {/* Form fields */}
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={`field-${i}`} class="space-y-2">
                  <Skeleton class="h-4 w-32 rounded" />
                  <Skeleton class="h-10 w-full rounded-md" />
                  <Skeleton class="h-3 w-3/4 rounded" />
                </div>
              ))}

              {/* Additional content area */}
              <div class="space-y-3">
                <Skeleton class="h-6 w-48 rounded" />
                <Skeleton class="h-32 w-full rounded-md" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
});

export default AgentFormSkeleton;
